import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RegistrationForm from './components/RegistrationForm';
import SocialRegistration from './components/SocialRegistration';
import RegistrationBenefits from './components/RegistrationBenefits';
import RegistrationSuccess from './components/RegistrationSuccess';

const CustomerRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form', 'success'
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  // Mock credentials for testing
  const mockCredentials = {
    email: 'test@voorbeeld.nl',
    password: 'Test123!',
    phone: '06 12345678',
    postalCode: '1012 AB'
  };

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock registration logic
      console.log('Registration data:', formData);
      
      setRegisteredEmail(formData?.email);
      setRegistrationStep('success');
    } catch (error) {
      console.error('Registration failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    setIsLoading(true);
    
    try {
      // Simulate social registration
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log(`Social registration with ${provider}`);
      
      // Mock successful social registration
      setRegisteredEmail('social@voorbeeld.nl');
      setRegistrationStep('success');
    } catch (error) {
      console.error('Social registration failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    
    try {
      // Simulate resend verification email
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Verification email resent');
    } catch (error) {
      console.error('Failed to resend verification:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleContinueToLogin = () => {
    navigate('/customer-login');
  };

  return (
    <>
      <Helmet>
        <title>Klant Registratie - Verduurzaamwijzer</title>
        <meta
          name="description"
          content="Maak een gratis account aan bij Verduurzaamwijzer en vind de beste duurzaamheidsproviders voor uw woning. Vergelijk offertes en bespaar op energiekosten."
        />
        <meta name="keywords" content="registratie, account aanmaken, duurzaamheid, zonnepanelen, warmtepomp, Nederland" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <MainNavigation />

        {/* Main Content */}
        <main className="flex-1 py-8 sm:py-12 pt-16 lg:pt-8 lg:ml-[72px] transition-all duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {registrationStep === 'form' ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Form Section */}
                <div className="lg:col-span-7">
                  <div className="max-w-2xl">
                    {/* Header */}
                    <div className="text-center lg:text-left mb-8">
                      <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
                        Maak uw gratis account aan
                      </h1>
                      <p className="text-lg font-body text-muted-foreground mb-6">
                        Begin uw duurzaamheidsreis en vind de beste providers voor uw woning
                      </p>
                      
                      {/* Already have account */}
                      <div className="flex items-center justify-center lg:justify-start space-x-2 text-sm font-body">
                        <span className="text-muted-foreground">
                          Heeft u al een account?
                        </span>
                        <Link 
                          to="/customer-login"
                          className="text-primary hover:text-primary/80 font-medium transition-micro"
                        >
                          Inloggen
                        </Link>
                      </div>
                    </div>

                    {/* Social Registration */}
                    <div className="mb-8">
                      <SocialRegistration 
                        onSocialRegister={handleSocialRegister}
                        isLoading={isLoading}
                      />
                    </div>

                    {/* Registration Form */}
                    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
                      <RegistrationForm 
                        onSubmit={handleFormSubmit}
                        isLoading={isLoading}
                      />
                    </div>

                    {/* Provider Registration Link */}
                    <div className="mt-8 text-center lg:text-left">
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                        <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
                          <Icon name="Building" size={18} color="var(--color-primary)" />
                          <span className="font-heading font-medium text-primary">
                            Bent u een provider?
                          </span>
                        </div>
                        <p className="text-sm font-body text-muted-foreground mb-3">
                          Registreer als duurzaamheidsprovider en bereik meer klanten
                        </p>
                        <Link to="/provider-registration">
                          <Button variant="outline" size="sm">
                            Provider worden
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Benefits Section */}
                <div className="lg:col-span-5">
                  <div className="sticky top-24">
                    <RegistrationBenefits />
                  </div>
                </div>
              </div>
            ) : (
              /* Success Step */
              (<div className="max-w-2xl mx-auto">
                <RegistrationSuccess
                  email={registeredEmail}
                  onResendVerification={handleResendVerification}
                  onContinue={handleContinueToLogin}
                  isResending={isResending}
                />
              </div>)
            )}
          </div>
        </main>

        {/* Footer Trust Signals */}
        <footer className="border-t border-border bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-6 mb-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} color="var(--color-success)" />
                  <span className="text-sm font-body text-muted-foreground">
                    GDPR Compliant
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Lock" size={16} color="var(--color-success)" />
                  <span className="text-sm font-body text-muted-foreground">
                    SSL Beveiligd
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={16} color="var(--color-success)" />
                  <span className="text-sm font-body text-muted-foreground">
                    KvK Geregistreerd
                  </span>
                </div>
              </div>
              <p className="text-xs font-body text-muted-foreground">
                © {new Date()?.getFullYear()} Verduurzaamwijzer. Alle rechten voorbehouden.
              </p>
            </div>
          </div>
        </footer>

        {/* Mock Credentials Display (Development Only) */}
      </div>
    </>
  );
};

export default CustomerRegistration;